import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * t05 — Feinschliff-Layer (Parallax/Tilt/Reveal-Erweiterungen)
 *
 * Bewusst getrennt von useScrollPinStack.ts: rührt weder die Pin-Progress-Formel
 * noch position:sticky/Spacer-Höhe an. Rein additive Deko-Hooks, alle mit
 * prefers-reduced-motion- und pointer:coarse-Fallback (Touch = aus).
 */

function useFineMotionAllowed(): boolean {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarse = window.matchMedia('(pointer: coarse)');
    const apply = () => setAllowed(!reduce.matches && !coarse.matches);
    apply();
    reduce.addEventListener('change', apply);
    coarse.addEventListener('change', apply);
    return () => {
      reduce.removeEventListener('change', apply);
      coarse.removeEventListener('change', apply);
    };
  }, []);

  return allowed;
}

/** Nav-Pill: Blur/Shadow verstärken sobald über die Hero hinaus gescrollt wurde. */
export function useNavScrollElevation(thresholdPx = 80): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrolled(window.scrollY > thresholdPx));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, [thresholdPx]);

  return scrolled;
}

/**
 * Cursor-Spotlight hinter der Hero — eigenständiges Layer (t05-hero-glow),
 * fasst die bestehenden data-parallax/data-parallax-bg-Orbs (useScrollParallax) nicht an.
 */
export function useHeroSpotlight<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const allowed = useFineMotionAllowed();

  useEffect(() => {
    const el = ref.current;
    if (!el || !allowed) return;

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--spot-x', `${x.toFixed(1)}%`);
      el.style.setProperty('--spot-y', `${y.toFixed(1)}%`);
      el.style.setProperty('--spot-o', '1');
    };
    const onLeave = () => el.style.setProperty('--spot-o', '0');

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [allowed]);

  return ref;
}

/** Hero-Hintergrundvideo: langsamer und dezenter (playbackRate). */
export function useHeroVideoPlayback(
  rate = 0.52,
): RefObject<HTMLVideoElement | null> {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    const applyRate = () => {
      video.playbackRate = rate;
    };

    const tryPlay = () => {
      applyRate();
      void video.play().catch(() => {
        /* autoplay policy — muted loop usually succeeds on retry */
      });
    };

    tryPlay();
    video.addEventListener('loadedmetadata', tryPlay);
    video.addEventListener('canplay', tryPlay);
    return () => {
      video.removeEventListener('loadedmetadata', tryPlay);
      video.removeEventListener('canplay', tryPlay);
    };
  }, [rate]);

  return ref;
}

/** Scroll-Fortschritt (0..1) als CSS-Var --scroll-progress auf dem Ziel-Element. */
export function useScrollProgress<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
        el.style.setProperty('--scroll-progress', p.toFixed(4));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return ref;
}

/**
 * Count-Up für `[data-countup]`-Werte, sobald sie ins Viewport scrollen.
 * Werte ohne führende Ziffer (z. B. "Edge") bleiben unangetastet.
 */
export function useStatsCountUp(containerRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const container = containerRef.current;
    if (!container || reduce) return;

    const targets = Array.from(container.querySelectorAll<HTMLElement>('[data-countup]'));
    if (targets.length === 0) return;

    const animate = (el: HTMLElement) => {
      const raw = el.dataset.countup ?? el.textContent ?? '';
      const match = raw.match(/^(\d+)(.*)$/);
      if (!match) return;
      const target = parseInt(match[1], 10);
      const suffix = match[2] ?? '';
      const duration = 900;
      const start = performance.now();

      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = `${Math.round(target * eased)}${suffix}`;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate(entry.target as HTMLElement);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 },
    );

    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [containerRef]);
}

const SECTION_MOTION_SELECTOR = '[data-section-motion]';

/**
 * Landing-only section choreography: fade/slide on enter, dim/retract when
 * scrolling back up past a section. IntersectionObserver only — no scroll listeners.
 */
export function useLandingSectionMotion(
  rootSelector = '.landing-template-t05',
): void {
  useEffect(() => {
    const root = document.querySelector(rootSelector);
    if (!root) return;

    const sections = Array.from(
      root.querySelectorAll<HTMLElement>(SECTION_MOTION_SELECTOR),
    );
    if (sections.length === 0) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      sections.forEach((el) => {
        el.classList.add('motion-enter');
        el.classList.remove('motion-recede');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          const { top, bottom } = entry.boundingClientRect;

          if (entry.isIntersecting) {
            el.classList.add('motion-enter');
            el.classList.remove('motion-recede');
            return;
          }

          /* Scrolled past upward — content recedes/darkens */
          if (bottom <= 0 || top < window.innerHeight * 0.12) {
            el.classList.remove('motion-enter');
            el.classList.add('motion-recede');
            return;
          }

          /* Below viewport — reset for next downward pass */
          if (top >= window.innerHeight) {
            el.classList.remove('motion-enter', 'motion-recede');
          }
        });
      },
      { threshold: [0, 0.06, 0.12], rootMargin: '0px 0px -6% 0px' },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [rootSelector]);
}
